/**
 * Formats a `Date` as a `YYYY-MM-DD` string in the runtime's local timezone.
 *
 * Defaults to `new Date()` so callers writing `toIsoDate()` get today. Used
 * for native `<input type="date">` values, `paidAt` defaults, and any other
 * place that needs the local calendar day as an ISO date - the machine-parseable
 * counterpart to `formatShortDate` (which is a presentational, UTC-anchored
 * renderer).
 */
export function toIsoDate(date = new Date()) {
    const y = date.getFullYear();
    const m = padDatePart(date.getMonth() + 1);
    const d = padDatePart(date.getDate());
    return `${y}-${m}-${d}`;
}
/**
 * Formats a YYYY-MM-DD string as a short en-ZA date, e.g. "24 Mar 2026".
 *
 * Uses `Intl.DateTimeFormat` with `timeZone: 'UTC'` to avoid local-timezone
 * drift when constructing the Date from year/month/day parts.
 */
export function formatShortDate(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    return new Intl.DateTimeFormat('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, day)));
}
/**
 * Formats a YYYY-MM-DD string as a long en-ZA date, e.g. "3 May 2026".
 *
 * Uses `Intl.DateTimeFormat` with `timeZone: 'UTC'` to avoid local-timezone
 * drift when constructing the Date from year/month/day parts.
 */
export function formatLongDate(iso) {
    const [year, month, day] = iso.split('-').map(Number);
    return new Intl.DateTimeFormat('en-ZA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, day)));
}
/**
 * Formats an ISO date-time string as a long en-ZA date, e.g. "3 May 2026",
 * for a **period boundary** - `currentPeriodEnd`, `currentPeriodStart`,
 * `trialEnd` - where the value names a nominal calendar day rather than the
 * instant something happened.
 *
 * Truncates to the UTC calendar day and delegates to {@link formatLongDate},
 * mirroring the `.slice(0, 10)` truncation already used at several call sites
 * (`importedAt`, `emailSentAt`) rather than parsing the full instant in the
 * runtime's local timezone - the date-time is anchored to UTC, so reading it
 * back in local time can drift the calendar day by one at the edges.
 *
 * Do not use this for a real instant (a `createdAt`, `cancelledAt`, or any
 * other timestamp of something that happened) - truncating to the UTC day
 * renders it a day early for two hours of every SA morning. Use
 * {@link formatLongDateTimeInSA} for that case instead.
 */
export function formatLongDateTime(isoDateTime) {
    return formatLongDate(isoDateTime.slice(0, 10));
}
/**
 * Formats an ISO date-time string as a long en-ZA date, e.g. "3 May 2026",
 * for a real **instant** - a `createdAt`, `cancelledAt`, or any other
 * timestamp of something that happened - by rendering it on its South
 * African calendar day (`Africa/Johannesburg`, a fixed UTC+2 offset with no
 * DST).
 *
 * Do not use this for a period boundary (`currentPeriodEnd`,
 * `currentPeriodStart`, `trialEnd`): those values name a nominal calendar
 * day rather than a real instant, and this SAST rendering would shift a
 * midnight-UTC boundary to the previous SA evening. Use
 * {@link formatLongDateTime} for that case instead.
 */
export function formatLongDateTimeInSA(isoDateTime) {
    return new Intl.DateTimeFormat('en-ZA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'Africa/Johannesburg',
    }).format(new Date(isoDateTime));
}
/**
 * Formats an ISO date-time string as a short en-ZA date, e.g. "03 May 2026",
 * for a **period boundary** - `currentPeriodEnd`, `trialEnd`, or any other
 * value that names a nominal calendar day rather than an instant.
 *
 * The short-form sibling of {@link formatLongDateTime}: truncates to the
 * UTC calendar day and delegates to {@link formatShortDate}. Do not use for
 * a real instant - see {@link formatShortDateTimeInSA}.
 */
export function formatShortDateTime(isoDateTime) {
    return formatShortDate(isoDateTime.slice(0, 10));
}
/**
 * Formats an ISO date-time string as a short en-ZA date, e.g. "03 May 2026",
 * for a real **instant** - a `createdAt`, `cancelledAt`, or any other
 * timestamp of something that happened - by rendering it on its South
 * African calendar day (`Africa/Johannesburg`, a fixed UTC+2 offset, no DST).
 *
 * The short-form sibling of {@link formatLongDateTimeInSA}. Do not use for
 * a period boundary - see {@link formatShortDateTime}.
 */
export function formatShortDateTimeInSA(isoDateTime) {
    return new Intl.DateTimeFormat('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'Africa/Johannesburg',
    }).format(new Date(isoDateTime));
}
/**
 * Formats an ISO date-time string as a short en-ZA date and 24-hour clock
 * time, e.g. "04 Oct 2026, 00:00", for a real **instant** rendered on its
 * South African calendar day and clock (`Africa/Johannesburg`, a fixed UTC+2
 * offset, no DST).
 *
 * Composes {@link formatShortDateTimeInSA} with the clock, and fixes the
 * separator rather than taking ICU's, which varies by runtime.
 */
export function formatShortDateWithTimeInSA(isoDateTime) {
    return `${formatShortDateTimeInSA(isoDateTime)}, ${formatClockTimeInSA(isoDateTime)}`;
}
/**
 * Formats an ISO date-time string as a long en-ZA date and 24-hour clock
 * time, e.g. "4 October 2026 at 00:00", for a real **instant** rendered on its
 * South African calendar day and clock (`Africa/Johannesburg`, a fixed UTC+2
 * offset, no DST).
 *
 * The long-form sibling of {@link formatShortDateWithTimeInSA}, for prose
 * ("Last edited ...") where the abbreviated month would read curtly.
 */
export function formatLongDateWithTimeInSA(isoDateTime) {
    return `${formatLongDateTimeInSA(isoDateTime)} at ${formatClockTimeInSA(isoDateTime)}`;
}
/**
 * Formats an ISO date-time string as its 24-hour South African clock time,
 * e.g. "00:00" (`Africa/Johannesburg`, a fixed UTC+2 offset, no DST).
 */
export function formatClockTimeInSA(isoDateTime) {
    return new Intl.DateTimeFormat('en-ZA', {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: 'Africa/Johannesburg',
    }).format(new Date(isoDateTime));
}
const SA_DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Names the South African calendar day of an instant relative to `now`:
 * "Today", "Tomorrow", or a weekday and date without the year, e.g.
 * "Sun 11 Oct". For a time a reader acts on within days, such as a next-run
 * time; pair it with {@link formatClockTimeInSA}.
 */
export function formatRelativeDayInSA(isoDateTime, now = new Date()) {
    const day = toSaIsoDate(new Date(isoDateTime));
    if (day === toSaIsoDate(now))
        return 'Today';
    if (day === toSaIsoDate(new Date(now.getTime() + SA_DAY_MS)))
        return 'Tomorrow';
    const parts = new Intl.DateTimeFormat('en-ZA', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        timeZone: 'Africa/Johannesburg',
    }).formatToParts(new Date(isoDateTime));
    const part = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${part('weekday')} ${part('day')} ${part('month')}`;
}
/**
 * Formats a `YYYY-MM` calendar-month identifier as an en-ZA month/year label,
 * e.g. "April 2026".
 *
 * Uses `Intl.DateTimeFormat` with `timeZone: 'UTC'` to avoid local-timezone
 * drift, mirroring `formatShortDate` / `formatLongDate` - the day-less
 * sibling for a report-period-set column header (`YearMonthSchema` in
 * `@packages/types`), which carries no day component to format.
 */
export function formatMonthYear(yearMonth) {
    const [year, month] = yearMonth.split('-').map(Number);
    return new Intl.DateTimeFormat('en-ZA', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, 1)));
}
/**
 * Formats a `Date` as a `YYYY-MM-DD` string using its **UTC** calendar day.
 *
 * The UTC-anchored counterpart to {@link toIsoDate}, which uses the runtime's
 * LOCAL day. The two disagree whenever local time and UTC fall on different
 * calendar dates - in SAST (UTC+2) that is 00:00 to 02:00 daily - so a module
 * must pick one and use it throughout. Mixing them produces an off-by-one-day
 * bug that is invisible in a UTC test runner and surfaces only for users east
 * or west of Greenwich, at the edges of the day.
 *
 * Use this wherever the surrounding arithmetic is UTC-based, i.e. anything
 * built from `Date.UTC(...)` or read via `getUTCFullYear` / `getUTCMonth` /
 * `getUTCDate`. Use {@link toIsoDate} for a local calendar day: a native
 * `<input type="date">` value, a `paidAt` default, "today" as the user's
 * device reckons it.
 */
export function toUtcIsoDate(date = new Date()) {
    const y = date.getUTCFullYear();
    const m = padDatePart(date.getUTCMonth() + 1);
    const d = padDatePart(date.getUTCDate());
    return `${y}-${m}-${d}`;
}
/**
 * The UTC-midnight `Date` for a `YYYY-MM-DD` calendar day: the value a
 * `@db.Date` column holds, and the inverse of {@link toUtcIsoDate}. The input
 * is not validated; callers pass a day their schema has already checked.
 */
export function fromUtcIsoDate(iso) {
    return new Date(`${iso}T00:00:00.000Z`);
}
function padDatePart(value) {
    return String(value).padStart(2, '0');
}
function isValidDayPrefix(value) {
    if (value.length === 1) {
        return /^[0-3]$/.test(value);
    }
    const day = Number(value);
    return Number.isInteger(day) && day >= 1 && day <= 31;
}
function isValidMonthPrefix(value) {
    if (value.length === 1) {
        return /^[0-1]$/.test(value);
    }
    const month = Number(value);
    return Number.isInteger(month) && month >= 1 && month <= 12;
}
/**
 * Whether the parts name a day that exists: false for 30 February, 31 April or
 * month 13, which `Date.UTC` would otherwise roll silently into the next month.
 * `month` is 1-based. Every parser that builds a date from printed parts checks
 * it here rather than re-implementing the round-trip.
 */
export function isRealCalendarDate(year, month, day) {
    const date = new Date(Date.UTC(year, month - 1, day));
    return (Number.isFinite(date.getTime()) &&
        date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day);
}
const MONTH_NAMES = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
];
/**
 * Resolves a printed English month name to a 0-based month index, or `null`.
 * Accepts the full name, the three-letter abbreviation, and `sept`,
 * case-insensitively; partial names other than those (`janu`) are rejected.
 */
export function monthIndexFromName(token) {
    const lower = token.trim().toLowerCase();
    if (lower === 'sept')
        return 8;
    const index = MONTH_NAMES.findIndex((name) => name === lower || name.slice(0, 3) === lower);
    return index === -1 ? null : index;
}
/**
 * Builds a `YYYY-MM-DD` string from numeric parts, or `null` when they are not
 * a real calendar date (31 April, month 13, a fractional day). The year is not
 * padded. Use it to turn parsed date parts into an ISO date; it does no parsing.
 */
export function buildIsoDate(year, month, day) {
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return null;
    }
    if (!isRealCalendarDate(year, month, day)) {
        return null;
    }
    return `${year}-${padDatePart(month)}-${padDatePart(day)}`;
}
/**
 * Parses a DD/MM/YYYY input string for the typeable calendar trigger.
 *
 * Partial means a clean prefix that can still become DD/MM/YYYY; malformed or
 * impossible prefixes are invalid so they do not move the calendar preview.
 */
export function parseDate(input) {
    if (input === '') {
        return { state: 'empty', iso: null };
    }
    const fullMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(input);
    if (fullMatch) {
        const [, dayRaw, monthRaw, yearRaw] = fullMatch;
        const day = Number(dayRaw);
        const month = Number(monthRaw);
        const year = Number(yearRaw);
        if (yearRaw === undefined ||
            monthRaw === undefined ||
            dayRaw === undefined ||
            year < 1000 ||
            !isValidDayPrefix(dayRaw) ||
            !isValidMonthPrefix(monthRaw) ||
            !isRealCalendarDate(year, month, day)) {
            return { state: 'invalid', iso: null };
        }
        return {
            state: 'valid',
            iso: `${yearRaw}-${padDatePart(month)}-${padDatePart(day)}`,
        };
    }
    if (!/^\d{1,2}(?:\/\d{1,2}(?:\/\d{1,3})?)?$/.test(input)) {
        return { state: 'invalid', iso: null };
    }
    const [dayRaw, monthRaw, yearRaw] = input.split('/');
    if (dayRaw === undefined ||
        !isValidDayPrefix(dayRaw) ||
        (monthRaw !== undefined && !isValidMonthPrefix(monthRaw))) {
        return { state: 'invalid', iso: null };
    }
    if (yearRaw !== undefined) {
        const month = Number(monthRaw);
        const day = Number(dayRaw);
        if (monthRaw === undefined ||
            !Number.isInteger(month) ||
            !Number.isInteger(day) ||
            !isRealCalendarDate(2000, month, day)) {
            return { state: 'invalid', iso: null };
        }
    }
    return { state: 'partial', iso: null };
}
export function formatDayInput(iso) {
    const [year, month, day] = iso.split('-');
    return `${day}/${month}/${year}`;
}
/**
 * Formats an instant as its `YYYY-MM-DD` South African calendar day
 * (`Africa/Johannesburg`, a fixed UTC+2 offset, no DST): the machine-parseable
 * counterpart to {@link formatShortDateTimeInSA}, for comparing an instant with
 * a date written as a `+02:00` timestamp. Between 00:00 and 02:00 SAST the UTC
 * day is still yesterday, which {@link toUtcIsoDate} would report.
 */
export function toSaIsoDate(date) {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Africa/Johannesburg' }).format(date);
}
